import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { adminApi, locationStatus, type LocationNode } from '../../lib/adminApi';
import { DeleteDialog } from './SuperAdminDialogs';
import { EditPlaceDialog } from './SuperAdminPlaceEdit';
import { PlaceRow } from './SuperAdminPlaceRow';
import { RetireDialog } from './RetireDialog';

interface FlatNode {
  node: LocationNode;
  depth: number;
}

/**
 * Depth-first, so the list reads Building → Floor → Room in the order it is nested —
 * but only descends into places the operator has opened.
 *
 * Everything used to render at once. One floor of sixteen rooms made the tab six
 * thousand pixels tall, which on a phone means the controls for the second floor are a
 * long scroll past the first, and the create form is off the top before you reach
 * anything. Collapsed by default: the top level is the part you navigate by.
 */
function visibleRows(nodes: LocationNode[], open: ReadonlySet<string>, depth = 0): FlatNode[] {
  return nodes.flatMap((node) => [
    { node, depth },
    ...(open.has(node.id) ? visibleRows(node.children, open, depth + 1) : []),
  ]);
}

/**
 * The room and place builder.
 *
 * Reads the tree from the ordinary admin endpoint — there is one property structure,
 * not a root-tier copy of it — but writes through `/api/super-admin/locations`, where
 * creating and retiring a place is recorded against the person who did it.
 *
 * Retiring is soft. The place keeps its id, so every ticket ever raised against it
 * still resolves to a name; it simply stops being offered for new work — and it can be
 * reinstated, which is what makes retirement the safe default.
 *
 * Deleting (D25) is not soft and cannot be undone. It is offered only for a place that
 * has never been used, and it is deliberately not adjacent to Retire in the row.
 */
export function SuperAdminRooms() {
  const { t } = useTranslation();
  const [tree, setTree] = useState<LocationNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  // One place, one act. Three separate `retiring | editing | deleting` states could each
  // be set independently, and nothing but luck stopped two dialogs stacking.
  const [acting, setActing] = useState<{ node: LocationNode; action: PlaceAction } | null>(null);
  // Ids of places whose children are shown. Empty to start — nothing is expanded until
  // it is asked for.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  /**
   * Opens a place, without the toggle's "or closes it".
   *
   * Adding a room to a collapsed floor otherwise puts it somewhere the operator cannot
   * see: the form clears, the count beside the floor goes up by one, and the room itself
   * is behind a chevron nobody was told to press. That reads as a create that half
   * worked. The place you just added to is the one place you certainly want open.
   */
  const reveal = useCallback((id: string) => {
    setOpen((current) => (current.has(id) ? current : new Set(current).add(id)));
  }, []);

  const load = useCallback(() => {
    // A root-tier screen reading `GET /api/admin/locations`, which now also requires
    // SUPER_ADMIN — so this reads straight through rather than relying on root
    // inheriting ADMIN, as it did while that endpoint sat on the admin tier (D23).
    // Worth stating because the `admin/` path makes it look like a cross-tier call and
    // the next reader would reasonably assume it is a mistake.
    adminApi.locations
      .tree()
      .then(setTree)
      .catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(load, [load]);

  /** Every mutation reports its own failure — a silent no-op reads as a broken button. */
  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setError(null);
      try {
        await action();
        load();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : t('superAdmin.failed'));
      }
    },
    [load, t],
  );

  if (!tree) {
    return error ? (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    ) : (
      <p className="text-sm text-slate-400">{t('superAdmin.loading')}</p>
    );
  }

  return (
    <div className="space-y-3">
      <AddPlaceForm
        parentId={parentId}
        onDone={() => {
          if (parentId) reveal(parentId);
          load();
        }}
        onError={setError}
      />

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {tree.length === 0 ? (
        <p className="text-sm text-slate-500">{t('superAdmin.rooms.empty')}</p>
      ) : (
        <ul className="rounded-xl border border-slate-800">
          {visibleRows(tree, open).map(({ node, depth }) => (
            <PlaceRow
              key={node.id}
              node={node}
              depth={depth}
              selected={parentId === node.id}
              onSelect={() => setParentId(parentId === node.id ? null : node.id)}
              onRetire={() => setActing({ node, action: 'retire' })}
              onDelete={() => setActing({ node, action: 'delete' })}
              onEdit={() => setActing({ node, action: 'edit' })}
              expanded={open.has(node.id)}
              onToggle={() => toggle(node.id)}
              onReinstate={() => run(() => adminApi.superAdmin.locations.reinstate(node.id))}
              onToggleMaintenance={() =>
                run(() =>
                  adminApi.superAdmin.locations.setStatus(node.id, {
                    status: locationStatus(node) === 'MAINTENANCE' ? 'ACTIVE' : 'MAINTENANCE',
                    reason: t('superAdmin.rooms.maintenanceReason'),
                  }),
                )
              }
            />
          ))}
        </ul>
      )}

      {acting && (
        <PlaceDialog
          {...acting}
          onClose={() => setActing(null)}
          onDone={() => {
            setActing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Which of the three confirmations a place is currently sitting in front of.
 *
 * Modelled as one value rather than three booleans because they are exclusive in fact:
 * a place cannot be mid-retire and mid-delete at once, and a stacked pair of dialogs
 * would put a Confirm button over a Confirm button for two different acts.
 */
type PlaceAction = 'retire' | 'edit' | 'delete';

function PlaceDialog({
  node,
  action,
  onClose,
  onDone,
}: {
  node: LocationNode;
  action: PlaceAction;
  onClose: () => void;
  onDone: () => void;
}) {
  // Reused as-is: retiring a place is exactly the act this dialog was written for.
  if (action === 'retire') {
    return (
      <RetireDialog
        subject={node.name}
        onConfirm={async (reason) => {
          await adminApi.superAdmin.locations.retire(node.id, reason);
          onDone();
        }}
        onCancel={onClose}
      />
    );
  }

  if (action === 'edit') {
    return <EditPlaceDialog node={node} onSaved={onDone} onCancel={onClose} />;
  }

  return (
    <DeleteDialog
      subject={node.name}
      confirmValue={node.name}
      explainKey="superAdmin.delete.explainPlace"
      onConfirm={async (reason, confirm) => {
        await adminApi.superAdmin.locations.remove(node.id, { reason, confirm });
        onDone();
      }}
      onCancel={onClose}
    />
  );
}

function AddPlaceForm({
  parentId,
  onDone,
  onError,
}: {
  parentId: string | null;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [qrCode, setQrCode] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;

    try {
      await adminApi.superAdmin.locations.create({
        name: name.trim(),
        parentId,
        qrCode: qrCode.trim() || null,
      });
      setName('');
      setQrCode('');
      onDone();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : t('superAdmin.failed'));
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-400">
        <Plus className="size-3.5" aria-hidden="true" />
        {parentId ? t('superAdmin.rooms.addInside') : t('superAdmin.rooms.addTop')}
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('superAdmin.rooms.namePlaceholder')}
          aria-label={t('superAdmin.rooms.namePlaceholder')}
          className="min-w-[140px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <input
          value={qrCode}
          onChange={(event) => setQrCode(event.target.value)}
          placeholder={t('superAdmin.rooms.qrPlaceholder')}
          aria-label={t('superAdmin.rooms.qrPlaceholder')}
          className="min-w-[120px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
      </div>
      <button
        type="submit"
        disabled={!name.trim()}
        className="mt-2 min-h-[48px] w-full rounded-lg bg-brookfields-green ring-1 ring-brookfields-gold/70 text-sm font-semibold text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
      >
        {t('superAdmin.rooms.add')}
      </button>
    </form>
  );
}
